import React, { useState } from 'react';
import { Card } from '../ui/Card';
import SafeIcon from '../../common/SafeIcon';
import { FiCheckCircle, FiChevronRight, FiPlay, FiRefreshCw } from 'react-icons/fi';

const steps = [
  { id: 1, title: 'Guest Arrival', desc: 'Party of 4 checks in at the host stand.', action: 'Assign Table T4' },
  { id: 2, title: 'Seat Table', desc: 'Update table status to "Occupied" in Real-time.', action: 'Confirm Seating' },
  { id: 3, title: 'Fire Order', desc: 'POS syncs tickets directly to Kitchen Prep Lanes.', action: 'Notify Kitchen' },
  { id: 4, title: 'Service Monitoring', desc: 'Track turn-time and service heatmap intensity.', action: 'View Metrics' },
  { id: 5, title: 'Checkout & Reset', desc: 'Table marked "Dirty" for busser priority.', action: 'Close Check' },
  { id: 6, title: 'Ready for Next', desc: 'Table reset to "Open". Waitlist notified.', action: 'Complete Flow' }
];

export const InteractiveFlow = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleNext = () => {
    if (currentStep < steps.length) {
      setIsProcessing(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsProcessing(false);
      }, 600);
    } else {
      setCurrentStep(1);
    }
  };

  return (
    <Card title="Try This Flow: Guest Cycle" className="border-chiliRed/30 shadow-glow-red/10">
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-2 mb-4">
            {steps.map(step => (
              <div 
                key={step.id} 
                className={`h-1 flex-1 rounded-full transition-colors duration-500 
                  ${step.id < currentStep ? 'bg-status-open' : step.id === currentStep ? 'bg-chiliRed' : 'bg-chefPanel'}`}
              />
            ))}
          </div>

          <div className="min-h-[120px]">
            <h4 className="text-receiptWhite font-bold text-lg flex items-center gap-2">
              <span className="text-chiliRed font-mono">0{currentStep}.</span>
              {steps[currentStep-1].title}
            </h4>
            <p className="text-receiptMuted text-sm mt-2 leading-relaxed">
              {steps[currentStep-1].desc}
            </p>
          </div>

          <button 
            onClick={handleNext}
            disabled={isProcessing}
            className="w-full flex items-center justify-center gap-2 bg-chiliRed hover:bg-chiliDark text-white py-3 rounded font-mono uppercase tracking-wider transition-all"
          >
            {isProcessing ? (
              <SafeIcon icon={FiRefreshCw} className="animate-spin" />
            ) : (
              <>
                {steps[currentStep-1].action}
                <SafeIcon icon={FiChevronRight} />
              </>
            )}
          </button>
        </div>

        <div className="w-full md:w-64 bg-chefBlack border border-chefPanel rounded p-4 flex flex-col justify-between">
          <div className="text-xs font-mono text-receiptMuted uppercase mb-4">Live System Status</div>
          <div className="space-y-3">
            <StatusRow label="Table T4" value={currentStep >= 2 ? 'Occupied' : 'Open'} active={currentStep >= 2} />
            <StatusRow label="Kitchen" value={currentStep >= 3 ? 'Active Ticket' : 'Idle'} active={currentStep >= 3} />
            <StatusRow label="Waitlist" value={currentStep >= 6 ? 'Next Notified' : 'Standby'} active={currentStep >= 6} />
          </div>
          <div className="mt-6 pt-4 border-t border-chefPanel text-[10px] text-receiptMuted italic">
            *Simulated workflow. No real POS connection required.
          </div>
        </div>
      </div>
    </Card>
  );
};

const StatusRow = ({ label, value, active }) => (
  <div className="flex justify-between items-center text-sm">
    <span className="text-receiptMuted">{label}</span>
    <span className={`${active ? 'text-chiliRed' : 'text-receiptMuted'} font-mono`}>{value}</span>
  </div>
);